import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrSection } from '../../../core/components/section/section';
import { LvrSimplePanel } from '../../../core/components/simple-panel/simple-panel';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { provideUserApi, USER_SERVICE } from '../../../api/services/user.service';
import { Representation } from '../../../api/app-types/representation.app';
import { MatIconModule } from '@angular/material/icon';
import { PERSON_TYPE, SearchResultItem } from '../../../api/app-types/search.app';
import { toSignal } from '@angular/core/rxjs-interop';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { User } from '../../../api/app-types/user.app';
import { HttpErrorResponse } from '@angular/common/http';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';

type RepresentationSortField = 'selected' | 'displayName' | 'role' | 'specialisation';
type SortDirection = 'asc' | 'desc';

@Component({
	selector: 'lvr-specialist-representation',
	imports: [
		TranslocoPipe,
		ReactiveFormsModule,
		MatButtonModule,
		MatCheckboxModule,
		MatIconModule,
		MatInputModule,
		MatMenuModule,
		MatSelectModule,
		MatTableModule,
		MatTooltipModule,
		LvrHeading,
		LvrSimplePanel,
		LvrSection,
		LvrDefaultActions,
	],
	providers: [provideUserApi],
	templateUrl: './specialist-representation.html',
	styleUrl: './specialist-representation.scss',
})
export class SpecialistRepresentation extends RouteInjection {
	private readonly userService = inject(USER_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly env = inject(ENVIRONMENT);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly userObjectId = this.resolveRouteParamAsSignal<string>('id');
	public readonly user = signal<User | null>(null);
	public readonly representations = signal<Representation[]>([]);
	public readonly possibleRepresentations = signal<Representation[]>([]);
	public readonly isEditing = signal<boolean>(false);
	public readonly selectedUserId = computed(() => this.user()?.id ?? '');
	public readonly hasRepresentations = computed(() => this.representations().length > 0);
	public readonly canEditSubstitutesPermission = hasPermission(this.session.permissions, 'Users.Substitutes.Edit');
	public readonly canReadSubstitutesPermission = hasPermission(this.session.permissions, 'Users.Substitutes.Read.Own');
	public readonly form = this.initForm(inject(FormBuilder));
	public readonly loading = signal<boolean>(false);

	public readonly displayedColumns = ['selected', 'displayName', 'role', 'specialisation'];

	public readonly sortField = signal<RepresentationSortField>('displayName');
	public readonly sortDirection = signal<SortDirection>('asc');
	public readonly activeSortField = signal<RepresentationSortField>('displayName');

	public readonly filterSearch = signal<Record<Exclude<RepresentationSortField, 'selected'>, string>>({
		displayName: '',
		role: '',
		specialisation: '',
	});

	private readonly participantsValue = toSignal(this.form.controls.participants.valueChanges, {
		initialValue: this.form.controls.participants.value,
	});
	public readonly selectedIds = computed(() => {
		const participants = (this.participantsValue() ?? []) as SearchResultItem[];
		return new Set(participants.map((p) => String(p.item.id)));
	});
	public readonly hasChanges = computed(() => {
		const currentIds = new Set(this.representations().map((rep) => rep.id));
		const selectedIds = this.selectedIds();

		// Check if selections are different from current representations
		if (currentIds.size !== selectedIds.size) return true;

		for (const id of currentIds) {
			if (!selectedIds.has(id)) return true;
		}

		return false;
	});

	public readonly possibleParticipants = computed(() => this.mapUsersToParticipants(this.possibleRepresentations()));

	public readonly filteredSortedPossibleRepresentations = computed(() => {
		let list = this.possibleRepresentations();

		const search = this.filterSearch();

		for (const key of ['displayName', 'role', 'specialisation'] as Exclude<RepresentationSortField, 'selected'>[]) {
			const term = (search[key] ?? '').trim().toLowerCase();

			if (!term) continue;

			list = list.filter((rep) => {
				const value =
					key === 'displayName'
						? this.getRepresentationDisplayName(rep)
						: String((rep as unknown as Record<string, unknown>)[key] ?? '');

				return value.toLowerCase().includes(term);
			});
		}

		const field = this.sortField();
		const direction = this.sortDirection();

		return [...list].sort((a, b) => {
			const aValue = this.getSortValue(a, field);
			const bValue = this.getSortValue(b, field);

			const comparison = aValue.localeCompare(bValue, 'de');
			return direction === 'asc' ? comparison : -comparison;
		});
	});

	public setFilterSearch(key: Exclude<RepresentationSortField, 'selected'>, value: string): void {
		this.filterSearch.update((search) => ({ ...search, [key]: value }));
	}

	public setActiveSortField(field: RepresentationSortField): void {
		this.activeSortField.set(field);
	}

	public setSort(field: RepresentationSortField, direction: SortDirection): void {
		this.sortField.set(field);
		this.sortDirection.set(direction);
	}

	public isSortOptionActive(field: RepresentationSortField, direction: SortDirection): boolean {
		return this.sortField() === field && this.sortDirection() === direction;
	}

	public sortLabel(direction: SortDirection): string {
		return direction === 'asc'
			? 'database_view.assignments.sort.alphabetical_ascending'
			: 'database_view.assignments.sort.alphabetical_descending';
	}

	public getRepresentationDisplayName(rep: Representation): string {
		return rep.displayName || `${rep.firstName ?? ''} ${rep.lastName ?? ''}`.trim() || '-';
	}

	public isSelected(rep: Representation): boolean {
		return this.selectedIds().has(String(rep.id));
	}

	public toggleRepresentation(rep: Representation, checked: boolean): void {
		const currentParticipants = this.form.controls.participants.value ?? [];

		if (checked) {
			const alreadySelected = currentParticipants.some((participant) => String(participant.item.id) === String(rep.id));

			if (alreadySelected) return;

			this.form.controls.participants.setValue([...currentParticipants, this.mapUserToParticipant(rep)]);

			return;
		}

		this.form.controls.participants.setValue(
			currentParticipants.filter((participant) => String(participant.item.id) !== String(rep.id)),
		);
	}

	public constructor() {
		super();

		effect(() => {
			const objectId = this.userObjectId();
			if (!objectId) return;

			void this.loadUserData(objectId);
		});
	}

	public startEdit() {
		this.form.controls.participants.setValue(this.mapUsersToParticipants(this.representations()));
		this.isEditing.set(true);
	}

	public cancelEdit() {
		this.form.controls.participants.setValue(this.mapUsersToParticipants(this.representations()));
		this.isEditing.set(false);
	}

	public async save() {
		const userId = this.selectedUserId();
		if (!userId) return;

		this.loading.set(true);

		try {
			await this.userService.updateRepresentations(userId, [...this.selectedIds()].map(String));
			await this.refreshData(userId);
		} catch {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.user_representation_update_error')),
			);
		} finally {
			this.isEditing.set(false);
			this.loading.set(false);
		}
	}

	private async refreshData(userId: string) {
		if (!userId) return;
		if (!this.canReadSubstitutesPermission()) {
			this.clearRepresentationState();
			return;
		}

		try {
			const representations = await this.userService.getRepresentations(
				userId,
				this.env.pagination.offset,
				this.env.pagination.range,
			);
			this.representations.set(representations.entities);
			this.form.controls.participants.setValue(this.mapUsersToParticipants(representations.entities));
		} catch (e) {
			if (e instanceof HttpErrorResponse && e.status === 403) {
				this.clearRepresentationState();
				this.ntfy.addNotification(
					createErrorNotification(this.transloco.translate('specialist.representation.no_permission_representations')),
				);
				return;
			}

			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('specialist.representation.permissions_load_error')),
			);
		}
	}

	private clearRepresentationState() {
		this.representations.set([]);
		this.possibleRepresentations.set([]);
		this.form.controls.participants.setValue([]);
		this.isEditing.set(false);
	}

	private async loadUserData(objectId: string) {
		try {
			const user = await this.userService.getSingle(objectId);
			this.user.set(user);
			this.isEditing.set(false);

			await this.refreshData(user.id);
			await this.loadPossibleRepresentations(user.id);
		} catch {
			this.user.set(null);
			this.clearRepresentationState();
		}
	}

	private async loadPossibleRepresentations(userId: string) {
		if (!userId) return;

		if (!this.canReadSubstitutesPermission()) {
			this.clearRepresentationState();
			return;
		}

		try {
			const res = await this.userService.getPossibleRepresentations(
				userId,
				this.env.pagination.offset,
				this.env.pagination.range,
			);
			this.possibleRepresentations.set(res.entities);
		} catch (e) {
			this.possibleRepresentations.set([]);

			if (e instanceof HttpErrorResponse && e.status === 403) {
				this.clearRepresentationState();
				this.ntfy.addNotification(
					createErrorNotification(this.transloco.translate('specialist.representation.no_permission_representations')),
				);
				return;
			}

			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('specialist.representation.permissions_load_error')),
			);
		}
	}

	private mapUserToParticipant(user: Representation): SearchResultItem {
		return {
			item: {
				id: Number(user.id),
				objectId: '',
				name: user.lastName || user.displayName,
				firstName: user.firstName,
				role: user.role,
			},
			personType: PERSON_TYPE.USER,
		};
	}

	private mapUsersToParticipants(users: Representation[]): SearchResultItem[] {
		return users.map((user) => this.mapUserToParticipant(user));
	}

	private getSortValue(rep: Representation, field: RepresentationSortField): string {
		if (field === 'selected') {
			return this.isSelected(rep) ? '1' : '0';
		}

		if (field === 'displayName') {
			return this.getRepresentationDisplayName(rep);
		}

		return String((rep as unknown as Record<string, unknown>)[field] ?? '');
	}

	private initForm(fb: FormBuilder) {
		return fb.group({
			participants: fb.control<SearchResultItem[]>([]),
		});
	}
}
